import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Container } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addToCart } from '../../store/cartSlice';
import { addToWishlist, removeFromWishlist } from '../../store/wishlistSlice';
import { categories } from '../../config/categories';
import { showShopToast } from '../../utils/shopToast';
import './RandomCategoryProducts.css';
import ProductImageGallery from './ProductImageGallery';

const API_URL = import.meta.env.VITE_API_URL;

function formatCOP(value) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

function shuffle(items) {
  return [...items].sort(() => Math.random() - 0.5);
}

function chunkItems(items, size) {
  const result = [];
  for (let i = 0; i < items.length; i += size) {
    result.push(items.slice(i, i + size));
  }
  return result;
}

function RandomCategoryProducts() {
  const TOTAL_PRODUCTS = 8;
  const PRODUCTS_PER_GRID = 4;
  const [products, setProducts] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const wishlistItems = useSelector((state) => state.wishlist.items || []);

  const featuredCategories = useMemo(() => categories.map((cat) => cat.id), []);

  const isInWishlist = (id) => wishlistItems.some((item) => item.id === id);

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    dispatch(addToCart(product));
    showShopToast(`${product.name} agregado al carrito`, 'success');
  };

  const handleToggleWishlist = (e, product) => {
    e.stopPropagation();
    if (isInWishlist(product.id)) {
      dispatch(removeFromWishlist(product.id));
      setToastMessage(`${product.name} eliminado de la lista de deseos`);
    } else {
      dispatch(addToWishlist(product));
      setToastMessage(`${product.name} agregado a la lista de deseos`);
    }
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleProductDetails = (productId) => {
    navigate(`/producto/${productId}`);
  };

  useEffect(() => {
    const loadRandomProducts = async () => {
      setLoading(true);
      try {
        const candidateCategories = shuffle(featuredCategories).slice(0, 6);

        const responses = await Promise.allSettled(
          candidateCategories.map(async (categoryId) => {
            const res = await fetch(`${API_URL}/destacados/categoria/${categoryId}`);
            if (!res.ok) return [];
            const data = await res.json();
            return data.map((product) => ({ ...product, sourceCategory: categoryId }));
          })
        );

        const groupedResults = responses
          .map((result) => (result.status === 'fulfilled' ? result.value : []))
          .filter((group) => group.length > 0);

        const onePerCategory = groupedResults
          .map((group) => shuffle(group)[0])
          .filter(Boolean);

        let selected = shuffle(onePerCategory).slice(0, TOTAL_PRODUCTS);

        if (selected.length < TOTAL_PRODUCTS) {
          const selectedIds = new Set(selected.map((product) => product.id));
          const fallbackPool = shuffle(groupedResults.flat()).filter((product) => !selectedIds.has(product.id));
          selected = [...selected, ...fallbackPool.slice(0, TOTAL_PRODUCTS - selected.length)];
        }

        if (selected.length < TOTAL_PRODUCTS) {
          const res = await fetch(`${API_URL}/destacados`);
          if (res.ok) {
            const data = await res.json();
            const selectedIds = new Set(selected.map((product) => product.id));
            const fallback = shuffle(data).filter((product) => !selectedIds.has(product.id));
            selected = [...selected, ...fallback.slice(0, TOTAL_PRODUCTS - selected.length)];
          }
        }

        setProducts(selected.slice(0, TOTAL_PRODUCTS));
      } catch (error) {
        console.error('Error cargando productos aleatorios por categorias:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    loadRandomProducts();
  }, [featuredCategories]);

  if (loading) {
    return (
      <section className="random-category-products py-5">
        <Container>
          <div className="featured-section-head text-center mb-4">
            <span className="featured-section-chip">Selecciones aleatorias</span>
            <h2 className="mb-2">Explora Categorias en Tendencia</h2>
            <p className="featured-section-subtitle mb-0">Cargando productos recomendados para ti...</p>
          </div>
        </Container>
      </section>
    );
  }

  if (!products.length) {
    return null;
  }

  const groupedProducts = chunkItems(products, PRODUCTS_PER_GRID);

  return (
    <section className="random-category-products py-5">
      <Container>
        <div className="featured-section-head text-center mb-4">
          <span className="featured-section-chip">Selecciones aleatorias</span>
          <h2 className="mb-2">Explora Categorias en Tendencia</h2>
          <p className="featured-section-subtitle mb-0">
            Ocho mini productos al azar, organizados en dos grids de cuatro, para descubrir nuevas opciones.
          </p>
        </div>

        {groupedProducts.map((group, groupIndex) => (
          <div className="random-category-grid" key={`group-${groupIndex}`}>
            {group.map((product) => {
              return (
                <Card
                  key={product.id}
                  className="random-product-card position-relative"
                  onClick={() => handleProductDetails(product.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="position-absolute top-0 end-0 p-2"
                    onClick={(e) => handleToggleWishlist(e, product)}
                    title={isInWishlist(product.id) ? 'Eliminar de la lista de deseos' : 'Agregar a la lista de deseos'}
                    style={{ zIndex: 10 }}
                  >
                    <i className={`bi bi-heart${isInWishlist(product.id) ? '-fill text-danger' : ''}`} style={{ fontSize: '1.25rem' }}></i>
                  </div>

                  <div className="random-product-image" onClick={(e) => e.stopPropagation()}>
                    <div className="random-product-image-stage">
                      <ProductImageGallery
                        imageUrl={product.imageUrl}
                        productName={product.name}
                        showPlayButton={false}
                        showFullscreenButton={false}
                        showThumbnails={false}
                        slideInterval={3200}
                        autoPlay
                      />
                    </div>
                  </div>

                  <Card.Body className="random-product-body">
                    <Card.Title className="random-product-title">{product.name}</Card.Title>
                    <Card.Text className="random-product-price mb-0">{formatCOP(product.priceCOP)}</Card.Text>

                    <Button
                      variant="success"
                      className="w-100 mt-2 random-add-cart-btn"
                      onClick={(e) => handleAddToCart(e, product)}
                    >
                      <i className="bi bi-cart-plus-fill me-2" aria-hidden="true"></i>
                      <span>Agregar al carrito</span>
                    </Button>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        ))}

        <div className="toast-container position-fixed bottom-0 end-0 p-3 shop-toast-container" style={{ zIndex: 9999 }}>
          <div
            className={`toast align-items-center text-bg-success border-0 ${showToast ? 'show' : 'hide'}`}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <div className="d-flex">
              <div className="toast-body">{toastMessage}</div>
              <button
                type="button"
                className="btn-close btn-close-white me-2 m-auto"
                onClick={() => setShowToast(false)}
              ></button>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
}

export default RandomCategoryProducts;
